/**
 * DOM helpers shared by the collectors.
 *
 * Everything is delegated from the document so markup that arrives later — AJAX cart
 * fragments, Elementor popups, the after-purchase flow — is covered without rebinding.
 */

export function on(target, type, selector, handler, options) {
  target.addEventListener(
    type,
    (event) => {
      const start = event.target;

      if (!start || typeof start.closest !== 'function') {
        return;
      }

      const match = start.closest(selector);

      if (match) {
        handler(event, match);
      }
    },
    options || false
  );
}

export function all(selector, root = document) {
  if (!selector) {
    return [];
  }

  return Array.prototype.slice.call(root.querySelectorAll(selector));
}

export function first(selector, root = document) {
  return selector ? root.querySelector(selector) : null;
}

/**
 * Calls back once an element is at least `ratio` visible. Falls back to firing
 * immediately where IntersectionObserver is missing (older in-app WebViews), because a
 * missing impression is worse than an optimistic one — spec §1 needs the denominator.
 */
export function whenVisible(elements, callback, { ratio = 0.5, win = window } = {}) {
  const list = Array.isArray(elements) ? elements : [elements];

  if (!list.length) {
    return null;
  }

  if (typeof win.IntersectionObserver !== 'function') {
    list.forEach((el) => callback(el));

    return null;
  }

  const observer = new win.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= ratio) {
          observer.unobserve(entry.target);
          callback(entry.target);
        }
      });
    },
    { threshold: ratio }
  );

  list.forEach((el) => observer.observe(el));

  return observer;
}

/**
 * First non-empty value among the given data attributes, walking up from the element.
 */
export function dataOf(element, names, fallback = null) {
  let node = element;

  while (node && node.getAttribute) {
    for (let i = 0; i < names.length; i++) {
      const value = node.getAttribute(names[i]);

      if (value !== null && value !== '') {
        return value;
      }
    }

    node = node.parentElement;
  }

  return fallback;
}

export function text(element, max = 120) {
  if (!element) {
    return '';
  }

  return (element.textContent || '').trim().replace(/\s+/g, ' ').slice(0, max);
}

/**
 * First non-empty value among the given attributes, on this element only.
 */
export function ownAttr(element, names, fallback = null) {
  if (!element || !element.getAttribute) {
    return fallback;
  }

  for (let i = 0; i < names.length; i++) {
    const value = element.getAttribute(names[i]);

    if (value !== null && value !== '') {
      return value;
    }
  }

  return fallback;
}

/**
 * Identifier for a block of markup, used for offer ids.
 *
 * Deliberately does NOT walk up the tree the way dataOf does: an offer card without an id
 * of its own would otherwise inherit the page-level data-product-id and every offer on the
 * page would share one key — and offer_shown is `once: 'session'` on that key, so only the
 * first card would ever be reported. For the same reason the last resort is the element's
 * position, not its first CSS class, which siblings share by definition.
 */
export function elementKey(element, index = 0) {
  if (!element) {
    return 'unknown';
  }

  return (
    ownAttr(element, ['data-td-offer-id', 'data-offer-id', 'data-product-id', 'data-order']) ||
    element.id ||
    'block-' + index
  );
}

export function jq(win = window) {
  return win.jQuery || null;
}

/**
 * Last amount in a rendered price string, in the formats the NF stores use:
 * `19,90 zł`, `1.299,00 zł`, `1 299,00 €`, `19.90`, `CHF 1'299.00`. The last number wins
 * because the label often starts with something else ("Kurier 24h: 15,00 zł").
 *
 * The apostrophes in the character class are the Swiss thousands separator. Without them
 * `CHF 1'299.00` tokenises as `1` and `299.00`, the last token wins, and every DOM-scraped
 * price on the CH store reads 100x low.
 *
 * Returns undefined when there is no amount at all — an absent price must not read as 0.
 */
export function parsePrice(value) {
  const tokens = String(value === undefined || value === null ? '' : value).match(
    /\d[\d\s\u00a0.,'\u2019]*\d|\d/g
  );

  if (!tokens) {
    return undefined;
  }

  const cleaned = tokens[tokens.length - 1].replace(/[\s\u00a0'\u2019]/g, '');
  const separator = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));

  // Three digits behind the last separator means it groups thousands, not decimals.
  const amount =
    separator === -1 || cleaned.length - separator - 1 === 3
      ? parseFloat(cleaned.replace(/[.,]/g, ''))
      : parseFloat(cleaned.slice(0, separator).replace(/[.,]/g, '') + '.' + cleaned.slice(separator + 1));

  return Number.isNaN(amount) ? undefined : amount;
}
